import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { useCustomersStore } from '@/stores/customers.store';
import { saveJson } from '@/lib/download';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { count, date, dateTime, money } from '@/lib/format';
import { statusTone } from '@/lib/order-status';
import { contactPhone, exportFileName } from '@/lib/customers';
import { returnReasonKey, returnStatusKey, returnStatusTone } from '@/lib/returns';
import { CartIcon, CoinsIcon, ReturnIcon, StarIcon, UsersIcon } from '@/components/icons';
import { TradeAccountPanel } from '@/components/trade-account-panel';
import { WalletPanel } from '@/components/wallet-panel';
import { LoyaltyPanel } from '@/components/loyalty-panel';
import { TagsField } from '@/components/fields';
import { DetailLayout, Page, Section, Sheet, Table, Td, Th } from '@/components/page';
import {
  Alert,
  Button,
  ButtonLink,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MetricCard,
  MetricSkeleton,
  Pill,
  Skeleton,
  Textarea,
  cx,
} from '@/components/ui';
import { ContactLinks } from './contact-links';
import { StagePill } from '@/components/customer-stage';
import { CustomerFollowUps, CustomerTimeline } from '@/components/customer-crm';
import { useFollowUpsStore } from '@/stores/follow-ups.store';
import type { CustomerRfmDto } from '@ecom/contracts';
import { useCustomerGroupsStore } from '@/stores/customer-groups.store';

/**
 * One customer.
 *
 * Built for the moment someone is on the phone: orders, returns, reviews and
 * the notes colleagues left, on one screen, because the question is never any
 * one of those on its own. What they did runs down the wide column; who they
 * are to the store — trade account, addresses, what they are about to buy, the
 * notes — sits beside it.
 *
 * Read-only apart from notes. Staff answer questions here; they do not edit a
 * customer's own details, and a password is neither readable nor settable.
 */
export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const {
    current,
    currentLoading,
    currentError,
    savingNote,
    privacyBusy,
    updating,
    tagsInUse,
    open,
    addNote,
    exportData,
    erase,
    update,
    loadTags,
  } = useCustomersStore();
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  // Reading notes comes with the page; writing one is support:manage.
  const mayWriteNotes = useAuthStore((state) => can(state.user, 'support:manage'));
  // Store credit is money: changed by whoever may refund (ADR-081).
  const mayAdjustCredit = useAuthStore((state) => can(state.user, 'refunds:manage'));
  const { memberOf, loadMemberOf } = useCustomerGroupsStore();
  const navigate = useNavigate();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const statusLabel = useStatusLabel();
  const [note, setNote] = useState('');
  const [confirmErase, setConfirmErase] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [confirmBlock, setConfirmBlock] = useState(false);

  // Product names in the reviews are resolved server-side, so a language
  // change is a refetch rather than a re-render.
  useEffect(() => {
    void open(id);
  }, [open, id, locale]);
  useEffect(() => {
    void loadMemberOf(id);
  }, [loadMemberOf, id]);
  // Their follow-ups and timeline (ADR-104), read again when the language changes a product's name.
  const openCrm = useFollowUpsStore((state) => state.openCustomer);
  useEffect(() => {
    void openCrm(id);
  }, [openCrm, id, locale]);

  const back = { to: '/customers', label: t('customers') };

  if (currentLoading && !current) {
    return (
      <Page title={t('customers')} back={back} width="wide">
        <MetricSkeleton />
        <DetailLayout main={<Skeleton className="h-72" />} aside={<Skeleton className="h-48" />} />
      </Page>
    );
  }

  if (currentError && !current) {
    return (
      <Page title={t('customers')} back={back} width="wide">
        <Alert>{currentError}</Alert>
      </Page>
    );
  }

  if (!current) return null;

  // Theirs, a saved address's, or the one on their last order.
  const phone = contactPhone(current) ?? current.lastOrderPhone ?? null;

  return (
    <Page
      title={current.name || current.email || current.phone || '—'}
      subtitle={
        <>
          <span dir="ltr">{current.email ?? t('noEmail')}</span>
          {phone ? (
            <>
              {' · '}
              <span dir="ltr" data-numeric>
                {phone}
              </span>
            </>
          ) : null}
          {' · '}
          {t('customerSince')} {date(current.createdAt, locale)}
          {current.birthday ? (
            <>
              {' · '}
              {t('customerBirthday')} {birthdayLabel(current.birthday, locale)}
            </>
          ) : null}
          {current.signInWith.length > 0 ? (
            <>
              {' · '}
              {t('customerSignsInWith', { providers: current.signInWith.map((provider) => (provider === 'google' ? 'Google' : 'Apple')).join(locale === 'ar' ? ' و' : ' & ') })}
            </>
          ) : null}
        </>
      }
      back={back}
      meta={
        <>
          {/* Where they are with the store (ADR-103), opening everyone else in the same stage. */}
          <Link to={`/customers?stage=${current.stage}`}>
            <StagePill stage={current.stage} />
          </Link>
          {current.emailVerified ? (
            <Pill tone="success">{t('emailVerified')}</Pill>
          ) : (
            <Pill tone="warning">{t('emailUnverified')}</Pill>
          )}
          {current.isActive ? null : <Pill tone="danger">{t('customerBlocked')}</Pill>}
          {/* The store's own words for them (ADR-064). */}
          {current.tags.map((tag) => (
            <Pill key={tag} tone="info">
              {tag}
            </Pill>
          ))}
          {/* The rule-based groups they are in (ADR-085), each opening its members. */}
          {memberOf?.customerId === current.id
            ? memberOf.groups.map((group) => (
                <Link key={group.id} to={`/customers?group=${group.id}`}>
                  <Pill tone="neutral">{group.name[locale] || group.name.en}</Pill>
                </Link>
              ))
            : null}
        </>
      }
      actions={
        <>
          {mayWriteNotes ? (
            <Button
              variant="secondary"
              onClick={() => {
                setEditName(current.name);
                setEditPhone(current.phone ?? '');
                setEditTags(current.tags);
                void loadTags();
                setEditing(true);
              }}
            >
              {t('editCustomer')}
            </Button>
          ) : null}
          {/* Blocking locks a person out of their own orders: the owner's call. */}
          {isOwner ? (
            <Button variant="secondary" disabled={updating} onClick={() => setConfirmBlock(true)}>
              {current.isActive ? t('blockCustomer') : t('unblockCustomer')}
            </Button>
          ) : null}
          <ContactLinks phone={phone} email={current.email} />
          {/* A request made to the store under PDPL (R10): owner only, audited by the API. */}
          {isOwner ? (
            <>
              <Button
                variant="secondary"
                disabled={privacyBusy}
                onClick={async () => {
                  const data = await exportData(current.id);
                  if (data) await saveJson(data, exportFileName(current, new Date()));
                }}
              >
                {t('exportCustomerData')}
              </Button>
              <Button variant="secondary" disabled={privacyBusy} onClick={() => setConfirmErase(true)}>
                {t('eraseCustomer')}
              </Button>
            </>
          ) : null}
        </>
      }
      width="wide"
    >
      {currentError ? <Alert>{currentError}</Alert> : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label={t('totalSpent')}
          value={money(current.totalSpent, locale)}
          hint={t('excludesCancelled')}
          tone="mint"
          icon={<CoinsIcon />}
        />
        <MetricCard
          label={t('orderCount')}
          value={count(current.orderCount, locale)}
          tone="sky"
          icon={<CartIcon />}
        />
        <MetricCard
          label={t('averageOrderValue')}
          value={money(current.averageOrderValue, locale)}
          tone="violet"
          icon={<CoinsIcon />}
        />
        <MetricCard
          label={t('lastOrder')}
          value={current.lastOrderAt ? date(current.lastOrderAt, locale) : '—'}
          hint={current.firstOrderAt ? `${t('firstOrder')} ${date(current.firstOrderAt, locale)}` : undefined}
          tone="amber"
          icon={<UsersIcon />}
        />
      </div>

      <DetailLayout
        main={
          <>
            <Section
              title={t('orders')}
              subtitle={
                // The API sends only the latest; more counted than listed means some are not shown.
                current.orderCount > current.recentOrders.length
                  ? t('customerOrdersCapped', { shown: count(current.recentOrders.length, locale) })
                  : t('customerOrdersHint')
              }
              action={
                // Every order, on the orders screen — searched by the address they were placed with.
                current.email && current.orderCount > 0 ? (
                  <ButtonLink to={`/orders?search=${encodeURIComponent(current.email)}`} variant="ghost" size="sm">
                    {t('viewAll')}
                  </ButtonLink>
                ) : null
              }
              flush
            >
              {current.recentOrders.length === 0 ? (
                <div className="border-t border-line">
                  <EmptyState title={t('noOrdersYet')} icon={<CartIcon />} />
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-line border-t border-line">
                  {current.recentOrders.map((order) => (
                    <li key={order.id}>
                      <Link
                        to={`/orders/${order.id}`}
                        className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-sunken"
                      >
                        <span data-numeric dir="ltr" className="text-sm font-medium text-ink">
                          {order.number}
                        </span>
                        <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
                        <span className="ms-auto text-xs text-ink-faint">
                          {order.placedAt ? date(order.placedAt, locale) : '—'}
                        </span>
                        <span data-numeric className="shrink-0 text-sm text-ink">
                          {money(order.grandTotal, locale)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t('repeatPurchases')} subtitle={t('repeatPurchasesHint')} flush>
              {current.repeatProducts.length === 0 ? (
                <p className="px-5 pb-5 text-sm text-ink-muted">{t('noRepeatPurchases')}</p>
              ) : (
                <div className="overflow-x-auto border-t border-line">
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('product')}</Th>
                        <Th>{t('ordersWithIt')}</Th>
                        <Th>{t('unitsBought')}</Th>
                        <Th align="end">{t('spent')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.repeatProducts.map((entry) => (
                        <tr key={entry.name}>
                          <Td className="text-ink">{entry.name}</Td>
                          <Td className="text-ink-muted">{count(entry.orders, locale)}</Td>
                          <Td className="text-ink-muted">{count(entry.units, locale)}</Td>
                          <Td align="end" className="text-ink">
                            {money(entry.spent, locale)}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {current.favouriteCategory ? (
                // The table's last row already draws the line above it.
                <p
                  className={cx(
                    'px-5 py-3 text-sm text-ink-muted',
                    current.repeatProducts.length === 0 && 'border-t border-line',
                  )}
                >
                  {t('favouriteCategory')}:{' '}
                  <span className="text-ink">{current.favouriteCategory.name}</span>{' '}
                  ({money(current.favouriteCategory.spent, locale)})
                </p>
              ) : null}
            </Section>

            <Section
              title={t('returns')}
              subtitle={t('customerReturnsHint')}
              action={
                // Returns search by email too, so the list keeps the ones from before they signed up.
                current.email && current.returns.length > 0 ? (
                  <Link
                    to={`/returns?search=${encodeURIComponent(current.email)}`}
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    {t('viewAll')}
                  </Link>
                ) : null
              }
              flush
            >
              {current.returns.length === 0 ? (
                <div className="border-t border-line">
                  <EmptyState title={t('noReturns')} icon={<ReturnIcon />} />
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-line border-t border-line">
                  {current.returns.map((entry) => (
                    <li key={entry.id}>
                      {/* Opens the return itself, beside the returns list. */}
                      <Link
                        to={`/returns?open=${entry.id}`}
                        className="flex flex-wrap items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-sunken"
                      >
                        <span data-numeric dir="ltr" className="text-sm font-medium text-ink">
                          {entry.number}
                        </span>
                        <Pill tone={returnStatusTone(entry.status)}>{t(returnStatusKey(entry.status))}</Pill>
                        <span className="text-xs text-ink-muted">{t(returnReasonKey(entry.reason))}</span>
                        <span className="ms-auto text-xs text-ink-faint">{date(entry.requestedAt, locale)}</span>
                        <span data-numeric className="text-sm text-ink">
                          {money(entry.amount, locale)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t('reviews')} subtitle={t('customerReviewsHint')} flush>
              {current.reviews.length === 0 ? (
                <div className="border-t border-line">
                  <EmptyState title={t('noReviews')} icon={<StarIcon />} />
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-line border-t border-line">
                  {current.reviews.map((review) => (
                    <li key={review.id}>
                      <Link
                        to="/reviews"
                        className="flex flex-wrap items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-sunken"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm text-ink">
                          {review.productName || '—'}
                        </span>
                        <span data-numeric className="text-sm text-warning">
                          {'★'.repeat(review.rating)}
                          <span className="text-ink-faint">{'★'.repeat(5 - review.rating)}</span>
                        </span>
                        <Pill
                          tone={
                            review.status === 'APPROVED'
                              ? 'success'
                              : review.status === 'REJECTED'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {review.status === 'APPROVED'
                            ? t('approved')
                            : review.status === 'REJECTED'
                              ? t('rejected')
                              : t('pendingQueue')}
                        </Pill>
                        <span className="text-xs text-ink-faint">{date(review.createdAt, locale)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <CustomerTimeline />
          </>
        }
        aside={
          <>
            <CustomerFollowUps
              customer={{ id: current.id, name: current.name || current.email || current.phone || '' }}
              orders={current.recentOrders.map((order) => ({ id: order.id, number: order.number }))}
              mayWrite={mayWriteNotes}
            />

            <RfmPanel rfm={current.rfm} />

            <WalletPanel customerId={current.id} canAdjust={mayAdjustCredit} />

            <LoyaltyPanel customerId={current.id} canAdjust={mayAdjustCredit} />

            <TradeAccountPanel customerId={current.id} />

            <Section title={t('addresses')} flush>
              {current.addresses.length === 0 ? (
                <div className="border-t border-line">
                  <EmptyState title={t('noAddresses')} icon={<UsersIcon />} />
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-line border-t border-line">
                  {current.addresses.map((address) => (
                    <li key={address.id} className="px-5 py-3 text-sm">
                      <p className="flex items-center gap-2 font-medium text-ink">
                        {address.fullName}
                        {address.isDefault ? <Pill tone="neutral">{t('defaultAddress')}</Pill> : null}
                      </p>
                      <p className="text-ink-muted">{address.line1}</p>
                      {address.line2 ? <p className="text-ink-muted">{address.line2}</p> : null}
                      <p className="text-ink-muted">
                        {[address.city, address.region].filter(Boolean).join(locale === 'ar' ? '، ' : ', ')}
                      </p>
                      <p data-numeric dir="ltr" className="text-ink-muted">
                        {address.phone}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* The live cart, because "stalled at checkout" and "has not been
                back in a month" need completely different phone calls. */}
            <Section
              title={t('currentCart')}
              subtitle={current.cart ? `${t('updated')} ${date(current.cart.updatedAt, locale)}` : undefined}
              flush
            >
              {!current.cart || current.cart.items.length === 0 ? (
                <p className="px-5 pb-5 text-sm text-ink-muted">{t('cartEmpty')}</p>
              ) : (
                <ul className="flex flex-col">
                  {current.cart.items.map((item) => (
                    <li
                      key={`${item.productId}-${item.sku}`}
                      className="flex items-baseline justify-between gap-3 border-t border-line px-5 py-3 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-ink">{item.name}</span>
                        <span dir="ltr" className="block text-xs text-ink-muted">
                          {item.sku}
                        </span>
                      </span>
                      {/* Each and how many, never multiplied here: the admin does not compute money (ADR-004). */}
                      <span data-numeric className="shrink-0 text-ink">
                        {count(item.quantity, locale)} × {money(item.unitPrice, locale)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t('wishlist')} subtitle={t('wishlistHint')} flush>
              <SavedList entries={current.wishlist} locale={locale} empty={t('noWishlist')} />
            </Section>

            <Section title={t('compareList')} subtitle={t('compareListHint')} flush>
              <SavedList entries={current.compare} locale={locale} empty={t('noCompare')} />
            </Section>

            <Section title={t('notes')} subtitle={t('notesHint')} flush>
              {mayWriteNotes ? (
                <div className="flex flex-col gap-3 border-t border-line p-5">
                  <Textarea
                    rows={3}
                    value={note}
                    placeholder={t('notePlaceholder')}
                    aria-label={t('notes')}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <div>
                    <Button
                      disabled={savingNote || note.trim().length === 0}
                      onClick={async () => {
                        if (await addNote(id, note.trim())) {
                          setNote('');
                          // The note is the newest thing in their timeline.
                          void openCrm(id);
                        }
                      }}
                    >
                      {savingNote ? t('saving') : t('addNote')}
                    </Button>
                  </div>
                </div>
              ) : null}

              {current.notes.length > 0 ? (
                <ul className="flex flex-col divide-y divide-line border-t border-line">
                  {current.notes.map((entry) => (
                    <li key={entry.id} className="px-5 py-3">
                      <p className="text-sm text-ink">{entry.body}</p>
                      <p className="mt-1 text-xs text-ink-faint">
                        {entry.authorName} · {dateTime(entry.createdAt, locale)}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Section>
          </>
        }
      />

      <ConfirmDialog
        open={confirmErase}
        title={t('eraseCustomerTitle')}
        body={t('eraseCustomerBody')}
        confirmLabel={t('eraseCustomer')}
        cancelLabel={t('cancel')}
        busy={privacyBusy}
        onConfirm={async () => {
          const done = await erase(current.id);
          setConfirmErase(false);
          if (done) navigate('/customers');
        }}
        onCancel={() => setConfirmErase(false)}
      />

      <ConfirmDialog
        open={confirmBlock}
        title={current.isActive ? t('blockCustomerTitle') : t('unblockCustomerTitle')}
        body={current.isActive ? t('blockCustomerBody') : t('unblockCustomerBody')}
        confirmLabel={current.isActive ? t('blockCustomer') : t('unblockCustomer')}
        cancelLabel={t('cancel')}
        tone={current.isActive ? 'danger' : 'primary'}
        busy={updating}
        onConfirm={async () => {
          await update(current.id, { isActive: !current.isActive });
          setConfirmBlock(false);
        }}
        onCancel={() => setConfirmBlock(false)}
      />

      <Sheet
        open={editing}
        title={t('editCustomerTitle')}
        onClose={() => setEditing(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={updating}>
              {t('cancel')}
            </Button>
            <Button
              disabled={updating || editName.trim() === ''}
              onClick={async () => {
                const ok = await update(current.id, {
                  name: editName.trim(),
                  // Empty clears it; unchanged is not sent, so a number is never re-typed by accident.
                  ...(editPhone.trim() !== (current.phone ?? '') ? { phone: editPhone.trim() || null } : {}),
                  ...(editTags.join('\u0000') !== current.tags.join('\u0000') ? { tags: editTags } : {}),
                });
                if (ok) setEditing(false);
              }}
            >
              {updating ? t('saving') : t('save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label={t('fullName')} htmlFor="customerName">
            <Input id="customerName" value={editName} onChange={(event) => setEditName(event.target.value)} />
          </Field>
          <Field label={t('mobile')} htmlFor="customerPhone" hint={t('customerPhoneHint')}>
            <Input
              id="customerPhone"
              dir="ltr"
              inputMode="tel"
              value={editPhone}
              placeholder={current.lastOrderPhone ?? ''}
              onChange={(event) => setEditPhone(event.target.value)}
            />
          </Field>
          <TagsField
            id="customerTags"
            label={t('customerTags')}
            hint={t('customerTagsHint')}
            value={editTags}
            onChange={setEditTags}
            suggestions={tagsInUse.map((entry) => entry.tag)}
          />
          {currentError ? <Alert>{currentError}</Alert> : null}
        </div>
      </Sheet>
    </Page>
  );
}

/** A wishlist or compare list, which differ only in what they mean. */
function SavedList({
  entries,
  locale,
  empty,
}: {
  entries: { productId: string; name: string; price: number; savedAt: string }[];
  locale: 'ar' | 'en';
  empty: string;
}) {
  if (entries.length === 0) {
    return <p className="px-5 pb-5 text-sm text-ink-muted">{empty}</p>;
  }

  return (
    <ul className="flex flex-col">
      {entries.map((entry) => (
        <li
          key={entry.productId}
          className="flex items-baseline justify-between gap-3 border-t border-line px-5 py-3 text-sm"
        >
          <span className="min-w-0">
            <span className="block truncate text-ink">{entry.name}</span>
            <span className="block text-xs text-ink-muted">{date(entry.savedAt, locale)}</span>
          </span>
          <span className="shrink-0 text-ink">{money(entry.price, locale)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Recency, frequency and spend against the store's other buyers (ADR-103):
 * five steps each, 5 the best fifth — three short bars rather than a code.
 */
function RfmPanel({ rfm }: { rfm: CustomerRfmDto | null }) {
  const t = useT();
  const rows = rfm
    ? ([
        ['rfmRecency', rfm.recency],
        ['rfmFrequency', rfm.frequency],
        ['rfmMonetary', rfm.monetary],
      ] as const)
    : [];

  return (
    <Section title={t('rfmTitle')} subtitle={rfm ? t('rfmHint') : t('rfmNone')}>
      {rfm ? (
        <dl className="flex flex-col gap-3">
          {rows.map(([label, score]) => (
            <div key={label} className="flex items-center gap-3">
              <dt className="w-28 shrink-0 text-sm text-ink-muted">{t(label)}</dt>
              <dd className="flex flex-1 items-center gap-1" aria-label={`${score} / 5`}>
                {[1, 2, 3, 4, 5].map((step) => (
                  <span key={step} className={cx('h-2 flex-1 rounded-full', step <= score ? 'bg-brand-600' : 'bg-surface-sunken')} />
                ))}
                <span data-numeric className="ms-2 w-4 text-sm font-semibold text-ink">
                  {score}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </Section>
  );
}

/** A shared birthday as words — "14 March" — never with a year (ADR-115). */
function birthdayLabel(birthday: { month: number; day: number }, locale: 'ar' | 'en'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, birthday.month - 1, birthday.day)),
  );
}
